// ABOUTME: Relation dashboard block showing the latest warehouse news message and the 3 most recent.
// ABOUTME: Hidden when the relation's warehouse has no published messages; read-more opens the viewer modal.

import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { tokens } from "../../../../theme";
import Text from "../../../../components/theme/text/Text";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import Subheading from "../../../../components/theme/text/Subheading";
import ReadOnlyTiptap from "../../../../components/theme/chat/ReadOnlyTiptap";
import MViewRelationNews from "../../../../components/global/Modals/MCreateRelationNews/MViewRelationNews";
import {
    useGetRelationNewsArchiveQuery,
    useMarkRelationNewsSeenMutation,
} from "../../../../newapi/warehouse/relationNewsSlice";
import { useGetCustomerLogoQuery } from "../../../../newapi/user/userSlice";

const formatDate = (raw, locale) => {
    if (!raw) return "";
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(locale || "nl-NL", { day: "numeric", month: "long", year: "numeric" });
};

const NewsBlock = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Size 4: the newest message renders on the left, the next 3 in the list.
    const { data: archive } = useGetRelationNewsArchiveQuery({ page: 1, size: 4 });
    const { data: customerLogo } = useGetCustomerLogoQuery();
    const [markSeen] = useMarkRelationNewsSeenMutation();

    const [viewItem, setViewItem] = useState(null);

    const items = archive?.data || [];
    const latest = items[0] || null;
    const rest = items.slice(1);

    // The latest message is fully visible on the dashboard, so mark it seen.
    useEffect(() => {
        if (latest && !latest.seen) {
            markSeen(latest.id);
        }
    // Intentional: runs once per latest message id; markSeen is a stable mutation trigger and does not need to be a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latest?.id]);

    if (items.length === 0) return null;

    const openItem = (item) => {
        setViewItem(item);
        if (!item.seen) markSeen(item.id);
    };

    const cardSx = {
        p: 3,
        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
        border: `1px solid ${colors.grey[100]}`,
        boxShadow: `0px 2px 4px ${colors.grey[200]}`,
        borderRadius: 4,
        overflow: "hidden",
    };

    return (
        <Box
            sx={{
                mt: 4,
                px: 4,
                display: "flex",
                gap: 4,
                "@media (max-width: 950px)": {
                    flexDirection: "column",
                    gap: 2,
                },
                "@media screen and (max-width: 47.9735em)": {
                    px: 3,
                },
            }}
        >
            {/* Latest message - 50%, highlighted like the dashboard's accented metric card */}
            <Box
                sx={{
                    ...cardSx,
                    flex: 1,
                    minWidth: 0,
                    border: `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                    boxShadow: `0px 2px 4px ${theme.palette.mode === "dark" ? colors.blue[200] : colors.primary[200]}`,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            fontFamily: "Roboto, sans-serif",
                            background: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                            color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                        }}
                    >
                        {t("news") || "Nieuws"}
                    </Box>
                    <Text light fontSize={15}>{formatDate(latest.publishedAt, i18n.language)}</Text>
                </Box>
                <Subheading>{latest.title}</Subheading>
                <Box sx={{ mt: 1.5 }}>
                    <ReadOnlyTiptap content={latest.body} colors={colors} />
                </Box>
            </Box>

            {/* Next 3 messages - 50%; the newest is already shown on the left */}
            {rest.length > 0 && (
            <Box sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
                <Subheading>{t("latest_news") || "Laatste nieuws"}</Subheading>
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column" }}>
                    {rest.map((item) => (
                        <Box
                            key={item.id}
                            sx={{
                                py: 1.25,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
                                "&:last-of-type": { borderBottom: "none" },
                            }}
                        >
                            {!item.seen && (
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                        background: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                    }}
                                />
                            )}
                            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                                <Text bold fontSize={14} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {item.title}
                                </Text>
                                <Text light fontSize={12}>{formatDate(item.publishedAt, i18n.language)}</Text>
                            </Box>
                            <Box sx={{ flexShrink: 0 }}>
                                <Shbutton
                                    className="s32"
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    variant="contained"
                                    onClick={() => openItem(item)}
                                >
                                    {t("read_more") || "Lees meer"}
                                </Shbutton>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
            )}

            <MViewRelationNews
                open={Boolean(viewItem)}
                handleClose={() => setViewItem(null)}
                newsItem={viewItem}
                logoUrl={customerLogo?.logoUrl ?? null}
            />
        </Box>
    );
};

export default NewsBlock;
